// API client.
//
// Same-origin `/api/...` in both dev (Vite proxies) and production (Fastify serves the
// build), so there is no base-URL configuration to get wrong.

class ApiError extends Error {
  constructor(status, body) {
    super(body?.message || body?.error || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// The session is an HttpOnly cookie, so the only way this code learns that it has expired is
// the API refusing a request. Listeners are told once per refusal and the app drops to
// signed-out — otherwise a customer whose session lapsed mid-session watches every panel on
// the page fail individually with no explanation.
const unauthorizedListeners = new Set();

async function req(path, opts = {}) {
  const res = await fetch(path, {
    ...opts,
    credentials: "same-origin",
    headers: { ...(opts.body ? { "Content-Type": "application/json" } : {}), ...(opts.headers || {}) },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (res.status === 401 && data?.error === "auth_required") {
    for (const fn of unauthorizedListeners) fn();
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

export const qs = (params) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "" || (Array.isArray(v) && !v.length)) continue;
    sp.set(k, Array.isArray(v) ? v.join(",") : String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const api = {
  listings: (params) => req(`/api/listings${qs(params)}`),
  listing: (id) => req(`/api/listings/${encodeURIComponent(id)}`),
  quote: (id, params) => req(`/api/listings/${encodeURIComponent(id)}/quote${qs(params)}`),
  quotes: (id, params) => req(`/api/listings/${encodeURIComponent(id)}/quotes${qs(params)}`),
  adhocQuote: (params) => req(`/api/quote${qs(params)}`),
  market: (params) => req(`/api/market${qs(params)}`),
  marketSummary: (params) => req(`/api/market/summary${qs(params)}`),
  makes: () => req("/api/meta/makes"),

  // Sign-in. There is no register call because there is nothing to register: the first time
  // an address asks for a link, the account exists.
  me: () => req("/api/auth/me"),
  requestLink: (body) => req("/api/auth/link", { method: "POST", body }),
  logout: () => req("/api/auth/logout", { method: "POST" }),
  updateMe: (body) => req("/api/auth/me", { method: "PATCH", body }),
  /** Subscribe to "the session is gone". Returns an unsubscribe, for useEffect. */
  onUnauthorized: (fn) => { unauthorizedListeners.add(fn); return () => unauthorizedListeners.delete(fn); },
  stats: () => req("/api/meta/stats"),
  rules: () => req("/api/meta/rules"),
  enquire: (body) => req("/api/enquiries", { method: "POST", body }),
  admin: {
    status: (token) => req("/api/admin/status", { headers: { Authorization: `Bearer ${token}` } }),
    enquiries: (token, params) => req(`/api/admin/enquiries${qs(params || {})}`, { headers: { Authorization: `Bearer ${token}` } }),
    update: (token, id, body) => req(`/api/admin/enquiries/${id}`, { method: "PATCH", body, headers: { Authorization: `Bearer ${token}` } }),
    ingest: (token, body) => req("/api/admin/ingest", { method: "POST", body: body || {}, headers: { Authorization: `Bearer ${token}` } }),
  },
};

export { ApiError };
